import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Megaphone, FileText, Trash2, CheckCircle2, XCircle, Plus, CalendarDays, ImagePlus, Truck, Pencil, Users, BookOpen, BarChart3, KeyRound, HandCoins } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["Tactical Apparel", "Morale Patches & Coins", "Range Gear & Accessories", "Coffee & Provisions", "Faith Publications"];
const EVENT_TYPES = ["Range Day", "Course", "Fellowship", "Family"];
const GALLERY_CATS = ["Range Days", "Marksmanship", "Gear", "Fellowship"];
const FILES_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const inputCls = "mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none";
const labelCls = "font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400";
const submitCls = "w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold py-3 hover:bg-gold-light transition-colors disabled:opacity-60";

const fmtErr = (e) => formatApiErrorDetail(e.response?.data?.detail);

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", sku: "", category: CATEGORIES[0], price: "", stock: 25, image: "", description: "", featured: false });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) || 0 });
      toast.success(`${form.name} added to the armory`);
      setForm({ name: "", sku: "", category: CATEGORIES[0], price: "", stock: 25, image: "", description: "", featured: false });
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    try { await api.delete(`/admin/products/${p.id}`); toast.success(`${p.name} removed`); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-products-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-gold/25 bg-card2/50 p-7 space-y-5 h-fit" data-testid="admin-product-form">
        <h3 className="font-display text-lg uppercase tracking-wider text-parchment flex items-center gap-2"><Plus className="w-5 h-5 text-gold" /> Add Product</h3>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={labelCls}>Name</Label><Input data-testid="ap-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
          <div><Label className={labelCls}>SKU</Label><Input data-testid="ap-sku" required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })} className={inputCls} placeholder="CC-APP-004" /></div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className={labelCls}>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ap-category" className={`${inputCls} rounded-none`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate2 border-gold/25 text-parchment">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className={labelCls}>Price $</Label><Input data-testid="ap-price" required type="number" step="0.01" min="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} /></div>
            <div><Label className={labelCls}>Stock</Label><Input data-testid="ap-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className={inputCls} /></div>
          </div>
        </div>
        <div><Label className={labelCls}>Image URL</Label><Input data-testid="ap-image" required type="url" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} placeholder="https://images.unsplash.com/..." /></div>
        <div><Label className={labelCls}>Description</Label><Textarea data-testid="ap-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></div>
        <label className="flex items-center gap-3 cursor-pointer text-sm text-stone-300">
          <input data-testid="ap-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" />
          Feature on the home page spotlight
        </label>
        <button type="submit" data-testid="ap-submit" disabled={busy} className={submitCls}>{busy ? "Adding..." : "Add to Armory"}</button>
      </form>
      <div className="lg:col-span-7 space-y-2" data-testid="admin-product-list">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-4 border border-gold/15 bg-card2/40 px-4 py-3" data-testid={`admin-product-${p.id}`}>
            <img src={p.image} alt="" className="w-12 h-12 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-condensed uppercase tracking-wider text-parchment truncate">{p.name}</div>
              <div className="font-mono text-[10px] text-stone-500 tracking-widest">{p.sku} &mdash; {p.category}</div>
            </div>
            <span className="font-mono text-gold text-sm">${p.price.toFixed(2)}</span>
            <button data-testid={`admin-product-delete-${p.id}`} onClick={() => remove(p)} className="p-2 text-stone-500 hover:text-crimson transition-colors" aria-label={`Delete ${p.name}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EventsTab() {
  const empty = { title: "", date: "", time: "", location: "", type: "Range Day", description: "", capacity: 40 };
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rosters, setRosters] = useState({});
  const load = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleRoster = async (ev) => {
    if (rosters[ev.id]) return setRosters((r) => ({ ...r, [ev.id]: null }));
    try {
      const { data } = await api.get(`/admin/events/${ev.id}/rsvps`);
      setRosters((r) => ({ ...r, [ev.id]: data }));
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const toggleAttend = async (ev, userId) => {
    try {
      const { data } = await api.post(`/admin/events/${ev.id}/attendance`, { user_id: userId });
      setRosters((r) => ({ ...r, [ev.id]: r[ev.id].map((m) => (m.id === userId ? { ...m, attended: data.attended } : m)) }));
      toast.success(data.attended ? "Marked present" : "Attendance removed");
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editId) {
        await api.patch(`/admin/events/${editId}`, form);
        toast.success("Event updated");
      } else {
        await api.post("/admin/events", form);
        toast.success(`${form.title} posted to the calendar`);
      }
      setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const edit = (ev) => {
    setEditId(ev.id);
    setForm({ title: ev.title, date: ev.date, time: ev.time, location: ev.location, type: ev.type, description: ev.description, capacity: ev.capacity });
    window.scrollTo(0, 0);
  };
  const remove = async (ev) => {
    try { await api.delete(`/admin/events/${ev.id}`); toast.success(`${ev.title} removed`); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-events-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-gold/25 bg-card2/50 p-7 space-y-5 h-fit" data-testid="admin-event-form">
        <h3 className="font-display text-lg uppercase tracking-wider text-parchment flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-gold" /> {editId ? "Edit Event" : "Post Event"}
        </h3>
        <div><Label className={labelCls}>Title</Label><Input data-testid="ae-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={labelCls}>Date</Label><Input data-testid="ae-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          <div><Label className={labelCls}>Time</Label><Input data-testid="ae-time" required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={inputCls} placeholder="08:00 - 13:00" /></div>
        </div>
        <div><Label className={labelCls}>Location</Label><Input data-testid="ae-location" required value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className={labelCls}>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="ae-type" className={`${inputCls} rounded-none`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate2 border-gold/25 text-parchment">{EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className={labelCls}>Capacity</Label><Input data-testid="ae-capacity" type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 40 })} className={inputCls} /></div>
        </div>
        <div><Label className={labelCls}>Description</Label><Textarea data-testid="ae-description" required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} /></div>
        <div className="flex gap-3">
          {editId && <button type="button" data-testid="ae-cancel" onClick={() => { setEditId(null); setForm(empty); }} className="px-5 py-3 border border-crimson/40 text-crimson font-condensed uppercase tracking-widest text-sm hover:bg-crimson hover:text-parchment transition-colors">Cancel</button>}
          <button type="submit" data-testid="ae-submit" disabled={busy} className={`flex-1 ${submitCls}`}>{busy ? "Saving..." : editId ? "Save Changes" : "Post Event"}</button>
        </div>
      </form>
      <div className="lg:col-span-7 space-y-2" data-testid="admin-event-list">
        {events.map((ev) => (
          <div key={ev.id}>
            <div className="flex items-center gap-4 border border-gold/15 bg-card2/40 px-5 py-4" data-testid={`admin-event-${ev.id}`}>
              <div className="font-mono text-gold text-sm tracking-widest w-20 shrink-0">{new Date(`${ev.date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "2-digit" }).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-condensed uppercase tracking-wider text-parchment truncate">{ev.title}</div>
                <div className="font-mono text-[10px] text-stone-500 tracking-widest uppercase">{ev.type} &mdash; {ev.location} &mdash; {ev.rsvps.length} RSVPs</div>
              </div>
              <a
                data-testid={`admin-event-roster-pdf-${ev.id}`}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/events/${ev.id}/roster.pdf`}
                target="_blank"
                rel="noreferrer"
                className="p-2 text-stone-500 hover:text-gold transition-colors"
                aria-label="Printable sign-in sheet"
                title="Printable sign-in sheet"
              ><FileText className="w-4 h-4" /></a>
              <button data-testid={`admin-event-roster-${ev.id}`} onClick={() => toggleRoster(ev)} className={`p-2 transition-colors ${rosters[ev.id] ? "text-gold" : "text-stone-500 hover:text-gold"}`} aria-label="View RSVP roster"><Users className="w-4 h-4" /></button>
              <button data-testid={`admin-event-edit-${ev.id}`} onClick={() => edit(ev)} className="p-2 text-stone-500 hover:text-gold transition-colors" aria-label="Edit event"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`admin-event-delete-${ev.id}`} onClick={() => remove(ev)} className="p-2 text-stone-500 hover:text-crimson transition-colors" aria-label="Delete event"><Trash2 className="w-4 h-4" /></button>
            </div>
            {rosters[ev.id] && (
              <div className="border border-t-0 border-gold/15 bg-obsidian/60 px-5 py-3" data-testid={`roster-${ev.id}`}>
                {rosters[ev.id].length === 0 ? (
                  <p className="text-xs text-stone-500 font-mono tracking-widest uppercase">No RSVPs yet</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {rosters[ev.id].map((r, i) => (
                      <div key={i} className="flex items-center gap-3 flex-wrap">
                        <button
                          data-testid={`attend-${ev.id}-${r.id}`}
                          onClick={() => toggleAttend(ev, r.id)}
                          className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                            r.attended ? "border-gold bg-gold/15 text-gold" : "border-stone-600 text-stone-500 hover:border-gold hover:text-gold"
                          }`}
                        >
                          <CheckCircle2 className="w-3 h-3" /> {r.attended ? "Present" : "Mark Present"}
                        </button>
                        <span className="font-mono text-xs text-stone-300">
                          {r.name}{r.callsign ? <span className="text-gold"> &ldquo;{r.callsign}&rdquo;</span> : ""} <span className="text-stone-500">— {r.tier}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", tag: "General", body: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/announcements").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/announcements", form);
      toast.success("Announcement posted to Members HQ");
      setForm({ title: "", tag: "General", body: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (a) => {
    try { await api.delete(`/admin/announcements/${a.id}`); toast.success("Announcement removed"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-announcements-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-gold/25 bg-card2/50 p-7 space-y-5 h-fit" data-testid="admin-announcement-form">
        <h3 className="font-display text-lg uppercase tracking-wider text-parchment flex items-center gap-2"><Megaphone className="w-5 h-5 text-gold" /> Post Announcement</h3>
        <div><Label className={labelCls}>Title</Label><Input data-testid="aa-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></div>
        <div>
          <Label className={labelCls}>Tag</Label>
          <Select value={form.tag} onValueChange={(v) => setForm({ ...form, tag: v })}>
            <SelectTrigger data-testid="aa-tag" className={`${inputCls} rounded-none`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate2 border-gold/25 text-parchment">{["General", "Range", "Event", "Store", "Chaplain"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className={labelCls}>Body</Label><Textarea data-testid="aa-body" required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} /></div>
        <button type="submit" data-testid="aa-submit" disabled={busy} className={submitCls}>{busy ? "Posting..." : "Post to Members"}</button>
      </form>
      <div className="lg:col-span-7 space-y-3" data-testid="admin-announcement-list">
        {items.map((a) => (
          <div key={a.id} className="border border-gold/15 bg-card2/40 px-6 py-5" data-testid={`admin-announcement-${a.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-obsidian bg-gold px-2 py-0.5 font-semibold">{a.tag}</span>
                <h4 className="font-condensed uppercase tracking-wider text-lg text-parchment mt-2">{a.title}</h4>
                <p className="text-sm text-stone-400 mt-1 leading-relaxed">{a.body}</p>
              </div>
              <button data-testid={`admin-announcement-delete-${a.id}`} onClick={() => remove(a)} className="p-2 text-stone-500 hover:text-crimson transition-colors shrink-0" aria-label="Delete announcement"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationsTab() {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/admin/applications").then(({ data }) => setApps(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const review = async (a, status) => {
    try { await api.patch(`/admin/applications/${a.id}`, { status }); toast.success(`${a.name} ${status}`); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const badge = (s) => s === "approved" ? "bg-gold/15 text-gold border-gold/40" : s === "rejected" ? "bg-crimson/15 text-crimson border-crimson/40" : "bg-stone-500/10 text-stone-400 border-stone-500/40";

  return (
    <div className="space-y-3" data-testid="admin-applications-tab">
      {apps.length === 0 && <p className="text-stone-500 text-sm py-10" data-testid="applications-empty">No applications on file yet.</p>}
      {apps.map((a) => (
        <div key={a.id} className="border border-gold/15 bg-card2/40 px-6 py-5" data-testid={`application-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <h4 className="font-condensed uppercase tracking-wider text-lg text-parchment">{a.name}</h4>
              <p className="font-mono text-xs text-stone-500 mt-0.5">{a.email}{a.phone ? ` — ${a.phone}` : ""}</p>
            </div>
            <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${badge(a.status)}`} data-testid={`application-status-${a.id}`}>{a.status}</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 font-mono text-[11px] uppercase tracking-widest text-stone-500">
            <span>Tier: <span className="text-gold">{a.tier}</span></span>
            <span>Experience: <span className="text-stone-300">{a.experience}</span></span>
            <span>Filed: {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </div>
          <p className="text-sm text-stone-400 leading-relaxed mt-3 border-l-2 border-gold/30 pl-4">{a.statement}</p>
          {a.bylaws_accepted && a.bylaws_accepted_at && (
            <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-gold" data-testid={`bylaws-affirmed-${a.id}`}>
              <CheckCircle2 className="w-3.5 h-3.5" />
              Bylaws affirmed — {new Date(a.bylaws_accepted_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
            </p>
          )}
          {a.status === "pending" && (
            <div className="flex gap-3 mt-4">
              <button data-testid={`application-approve-${a.id}`} onClick={() => review(a, "approved")} className="flex items-center gap-2 bg-gold text-obsidian font-condensed uppercase tracking-widest text-sm font-semibold px-5 py-2 hover:bg-gold-light transition-colors"><CheckCircle2 className="w-4 h-4" /> Approve</button>
              <button data-testid={`application-reject-${a.id}`} onClick={() => review(a, "rejected")} className="flex items-center gap-2 border border-crimson/50 text-crimson font-condensed uppercase tracking-widest text-sm px-5 py-2 hover:bg-crimson hover:text-parchment transition-colors"><XCircle className="w-4 h-4" /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState(GALLERY_CATS[0]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("caption", caption);
      fd.append("category", category);
      fd.append("file", file);
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo published to the gallery");
      setCaption(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (g) => {
    try { await api.delete(`/admin/gallery/${g.id}`); toast.success("Photo removed"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };
  const src = (g) => g.url || `${FILES_BASE}${g.storage_path}`;

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-gallery-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-gold/25 bg-card2/50 p-7 space-y-5 h-fit" data-testid="admin-gallery-form">
        <h3 className="font-display text-lg uppercase tracking-wider text-parchment flex items-center gap-2"><ImagePlus className="w-5 h-5 text-gold" /> Upload Photo</h3>
        <div>
          <Label className={labelCls}>Photo (max 10MB)</Label>
          <input
            ref={fileRef}
            data-testid="ag-file"
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-1.5 w-full text-sm text-stone-400 file:mr-4 file:py-2.5 file:px-4 file:border file:border-gold/40 file:bg-transparent file:text-gold file:font-condensed file:uppercase file:tracking-widest file:text-xs hover:file:bg-gold hover:file:text-obsidian file:transition-colors file:cursor-pointer"
          />
        </div>
        <div><Label className={labelCls}>Caption</Label><Input data-testid="ag-caption" required value={caption} onChange={(e) => setCaption(e.target.value)} className={inputCls} placeholder="September muster — final string" /></div>
        <div>
          <Label className={labelCls}>Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="ag-category" className={`${inputCls} rounded-none`}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate2 border-gold/25 text-parchment">{GALLERY_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <button type="submit" data-testid="ag-submit" disabled={busy} className={submitCls}>{busy ? "Uploading..." : "Publish to Gallery"}</button>
      </form>
      <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="admin-gallery-list">
        {items.map((g) => (
          <div key={g.id} className="group relative border border-gold/15 overflow-hidden" data-testid={`admin-gallery-item-${g.id}`}>
            <img src={src(g)} alt={g.caption} className="w-full aspect-square object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-obsidian/85 px-3 py-2">
              <p className="font-condensed uppercase tracking-wider text-xs text-parchment truncate">{g.caption}</p>
            </div>
            <button data-testid={`admin-gallery-delete-${g.id}`} onClick={() => remove(g)} className="absolute top-2 right-2 p-1.5 bg-obsidian/80 text-stone-400 hover:text-crimson transition-colors" aria-label="Delete photo"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const advance = async (o, status) => {
    try {
      await api.patch(`/admin/orders/${o.id}`, { status });
      toast.success(status === "shipped" ? `Order marked shipped — member notified by email` : `Order marked ${status}`);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const badge = (s) => ({
    paid: "bg-gold/15 text-gold border-gold/40",
    packed: "bg-blue-500/10 text-blue-300 border-blue-500/40",
    shipped: "bg-crimson/15 text-crimson border-crimson/40",
    delivered: "bg-green-500/10 text-green-300 border-green-500/40",
    pending_payment: "bg-stone-500/10 text-stone-500 border-stone-500/30",
  }[s] || "bg-stone-500/10 text-stone-400 border-stone-500/40");
  const nextStep = { paid: ["packed", "Mark Packed"], packed: ["shipped", "Mark Shipped"], shipped: ["delivered", "Mark Delivered"] };

  return (
    <div className="space-y-3" data-testid="admin-orders-tab">
      {orders.length === 0 && <p className="text-stone-500 text-sm py-10" data-testid="orders-empty">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} className={`border px-6 py-5 ${o.status === "pending_payment" ? "border-gold/10 opacity-60" : "border-gold/15 bg-card2/40"}`} data-testid={`order-${o.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="font-mono text-sm text-parchment tracking-widest">№ {o.id.slice(-8).toUpperCase()}</span>
              <span className="font-mono text-xs text-stone-500 ml-4">{o.email}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-mono text-gold">${o.total.toFixed(2)}</span>
              <span className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${badge(o.status)}`} data-testid={`order-status-${o.id}`}>{o.status.replace("_", " ")}</span>
            </div>
          </div>
          <p className="text-sm text-stone-400 mt-2">{o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}</p>
          <p className="font-mono text-[11px] text-stone-500 mt-1.5 tracking-wider uppercase">
            Ship to: {o.shipping?.name}, {o.shipping?.address}, {o.shipping?.city} {o.shipping?.state} {o.shipping?.zip}
          </p>
          {nextStep[o.status] && (
            <button
              data-testid={`order-advance-${o.id}`}
              onClick={() => advance(o, nextStep[o.status][0])}
              className="mt-4 flex items-center gap-2 bg-gold text-obsidian font-condensed uppercase tracking-widest text-sm font-semibold px-5 py-2 hover:bg-gold-light transition-colors"
            >
              <Truck className="w-4 h-4" /> {nextStep[o.status][1]}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function DevotionsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", verse: "", body: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/devotions").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/devotions", form);
      toast.success("Devotion published");
      setForm({ title: "", verse: "", body: "" });
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    try { await api.delete(`/admin/devotions/${d.id}`); toast.success("Devotion removed"); load(); }
    catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-devotions-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-gold/25 bg-card2/50 p-7 space-y-5 h-fit" data-testid="admin-devotion-form">
        <h3 className="font-display text-lg uppercase tracking-wider text-parchment flex items-center gap-2"><BookOpen className="w-5 h-5 text-gold" /> Publish Devotion</h3>
        <div><Label className={labelCls}>Title</Label><Input data-testid="ad-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="The Weight of the Watch" /></div>
        <div><Label className={labelCls}>Scripture</Label><Input data-testid="ad-verse" required value={form.verse} onChange={(e) => setForm({ ...form, verse: e.target.value })} className={inputCls} placeholder="Nehemiah 4:9 — We prayed to our God..." /></div>
        <div><Label className={labelCls}>Body</Label><Textarea data-testid="ad-body" required rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={inputCls} /></div>
        <button type="submit" data-testid="ad-submit" disabled={busy} className={submitCls}>{busy ? "Publishing..." : "Publish Devotion"}</button>
      </form>
      <div className="lg:col-span-7 space-y-3" data-testid="admin-devotion-list">
        {items.map((d) => (
          <div key={d.id} className="border border-gold/15 bg-card2/40 px-6 py-5" data-testid={`admin-devotion-${d.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h4 className="font-condensed uppercase tracking-wider text-lg text-parchment">{d.title}</h4>
                <p className="font-serif italic text-sm text-gold/80 mt-1">{d.verse}</p>
                <p className="text-sm text-stone-400 mt-2 leading-relaxed line-clamp-2">{d.body}</p>
              </div>
              <button data-testid={`admin-devotion-delete-${d.id}`} onClick={() => remove(d)} className="p-2 text-stone-500 hover:text-crimson transition-colors shrink-0" aria-label="Delete devotion"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/reports/attendance").then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const max = Math.max(1, ...rows.map((r) => r.attended));

  return (
    <div data-testid="admin-reports-tab">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-stone-400">Season participation — RSVPs vs. verified attendance per member.</p>
      </div>
      {loading ? (
        <div className="h-40 bg-card2/50 border border-gold/10 animate-pulse" />
      ) : (
        <div className="border border-gold/15" data-testid="attendance-table">
          <div className="hidden sm:grid grid-cols-12 gap-3 px-5 py-3 bg-obsidian border-b border-gold/15 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
            <span className="col-span-4">Member</span>
            <span className="col-span-2 text-center">RSVPs</span>
            <span className="col-span-2 text-center">Attended</span>
            <span className="col-span-4">Participation</span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-2 sm:grid-cols-12 gap-3 items-center px-5 py-3.5 border-b border-gold/10 bg-card2/40 hover:bg-card2/70 transition-colors" data-testid={`report-row-${r.id}`}>
              <div className="col-span-2 sm:col-span-4">
                <span className="font-condensed uppercase tracking-wider text-parchment">{r.name}</span>
                {r.callsign && <span className="font-mono text-[10px] text-gold tracking-widest ml-2 uppercase">"{r.callsign}"</span>}
                <div className="font-mono text-[9px] text-stone-500 tracking-widest uppercase">{r.tier}</div>
              </div>
              <span className="font-mono text-sm text-stone-300 text-left sm:text-center sm:col-span-2">{r.rsvped}</span>
              <span className={`font-mono text-sm text-left sm:text-center sm:col-span-2 ${r.attended > 0 ? "text-gold font-semibold" : "text-stone-500"}`}>{r.attended}</span>
              <div className="col-span-2 sm:col-span-4">
                <div className="h-1.5 bg-obsidian overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-gold to-crimson transition-[width] duration-700" style={{ width: `${(r.attended / max) * 100}%` }} />
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && <p className="text-stone-500 text-sm px-5 py-8" data-testid="reports-empty">No members on record.</p>}
        </div>
      )}
    </div>
  );
}

const MEMBER_TIERS = ["Standard Member", "Tactical Squad", "Benefactor Sponsor"];

function MembersTab() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const load = () => api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setConfirmDeleteId(null);
    setEditingId(m.id);
    setDraft({
      name: m.name, email: m.email, callsign: m.callsign || "", specialty: m.specialty || "",
      phone: m.phone || "", role: m.role, tier: m.tier || MEMBER_TIERS[0],
      directory_visible: m.directory_visible !== false,
    });
  };
  const saveEdit = async (m) => {
    try {
      await api.patch(`/admin/members/${m.id}`, { ...draft, name: draft.name.trim(), email: draft.email.trim() });
      toast.success(`${draft.name.trim()} updated`);
      setEditingId(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const resendLogin = async (m) => {
    try {
      await api.post(`/admin/members/${m.id}/resend-login`);
      toast.success(`One-time login link sent to ${m.email}`);
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const approve = async (m) => {
    try {
      await api.post(`/admin/members/${m.id}/approve`);
      toast.success(`${m.name} approved — Members HQ unlocked`);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const remove = async (m) => {
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success(`${m.name} removed from the roster`);
      setConfirmDeleteId(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const filtered = members.filter((m) => `${m.name} ${m.email} ${m.callsign}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-members-tab">
      <div className="mb-6 max-w-sm">
        <Input data-testid="member-search" placeholder="Search name, email, or callsign..." value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} mt-0`} />
      </div>
      <div className="border border-gold/15 divide-y divide-gold/10">
        {filtered.map((m) => (
          <div key={m.id} className="px-5 py-4 bg-card2/40 hover:bg-card2/70 transition-colors" data-testid={`member-row-${m.id}`}>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="min-w-[180px]">
                <span className="font-condensed uppercase tracking-wider text-parchment" data-testid={`member-name-${m.id}`}>{m.name}</span>
                {m.callsign && <span className="font-mono text-[10px] text-gold tracking-widest ml-2 uppercase">"{m.callsign}"</span>}
                <div className="font-mono text-[9px] text-stone-500 tracking-widest uppercase mt-0.5">
                  {m.role === "admin" ? "Officer" : m.tier || "Member"}{m.dues_status ? ` • ${m.dues_status}` : ""}{m.auth_provider === "google" ? " • Google" : ""}
                </div>
              </div>
              <div className="flex-1 min-w-[220px]">
                <span className="font-mono text-xs text-stone-400" data-testid={`member-email-${m.id}`}>{m.email}</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                {m.approved === false && (
                  <>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-crimson border border-crimson/40 px-2 py-1" data-testid={`member-pending-badge-${m.id}`}>Pending</span>
                    <button data-testid={`member-approve-${m.id}`} onClick={() => approve(m)} className="font-condensed uppercase tracking-[0.15em] text-xs px-3 py-2 bg-gold text-obsidian font-semibold hover:bg-gold-light transition-colors">
                      Approve
                    </button>
                  </>
                )}
                <button data-testid={`member-edit-${m.id}`} onClick={() => (editingId === m.id ? setEditingId(null) : startEdit(m))} className="p-2 border border-gold/20 text-stone-400 hover:text-gold hover:border-gold/50 transition-colors" aria-label="Edit member" title="Edit member"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`member-resend-login-${m.id}`} onClick={() => resendLogin(m)} className="p-2 border border-gold/20 text-stone-400 hover:text-gold hover:border-gold/50 transition-colors" aria-label="Send one-time login link" title="Send one-time login link"><KeyRound className="w-4 h-4" /></button>
                {m.id !== user.id && (
                  <button
                    data-testid={confirmDeleteId === m.id ? `member-delete-confirm-${m.id}` : `member-delete-${m.id}`}
                    onClick={() => (confirmDeleteId === m.id ? remove(m) : (setEditingId(null), setConfirmDeleteId(m.id)))}
                    className={`p-2 border transition-colors ${confirmDeleteId === m.id ? "border-crimson bg-crimson/20 text-crimson font-condensed uppercase tracking-[0.15em] text-xs px-3" : "border-gold/20 text-stone-400 hover:text-crimson hover:border-crimson/50"}`}
                    aria-label={confirmDeleteId === m.id ? "Confirm remove member" : "Remove member"}
                    title={confirmDeleteId === m.id ? "Click again to confirm" : "Remove member"}
                  >
                    {confirmDeleteId === m.id ? "Confirm remove" : <Trash2 className="w-4 h-4" />}
                  </button>
                )}
              </div>
            </div>

            {editingId === m.id && draft && (
              <form onSubmit={(e) => { e.preventDefault(); saveEdit(m); }} className="mt-4 pt-4 border-t border-gold/10" data-testid={`member-edit-form-${m.id}`}>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label className={labelCls}>Full Name</Label><Input data-testid={`member-name-input-${m.id}`} required minLength={2} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>Email</Label><Input data-testid={`member-email-input-${m.id}`} required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>Callsign</Label><Input data-testid={`member-callsign-input-${m.id}`} value={draft.callsign} onChange={(e) => setDraft({ ...draft, callsign: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>Specialty</Label><Input data-testid={`member-specialty-input-${m.id}`} value={draft.specialty} onChange={(e) => setDraft({ ...draft, specialty: e.target.value })} className={inputCls} /></div>
                  <div><Label className={labelCls}>Phone</Label><Input data-testid={`member-phone-input-${m.id}`} value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className={inputCls} /></div>
                  <div>
                    <Label className={labelCls}>Role</Label>
                    <Select value={draft.role} onValueChange={(v) => setDraft({ ...draft, role: v })} disabled={m.id === user.id}>
                      <SelectTrigger data-testid={`member-role-select-${m.id}`} className={`${inputCls} rounded-none`} title={m.id === user.id ? "You cannot change your own role" : ""}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate2 border-gold/25 text-parchment">
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="admin">Officer (Admin)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Membership Tier</Label>
                    <Select value={draft.tier} onValueChange={(v) => setDraft({ ...draft, tier: v })}>
                      <SelectTrigger data-testid={`member-tier-select-${m.id}`} className={`${inputCls} rounded-none`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate2 border-gold/25 text-parchment">
                        {MEMBER_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input type="checkbox" data-testid={`member-directory-toggle-${m.id}`} checked={draft.directory_visible} onChange={(e) => setDraft({ ...draft, directory_visible: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" />
                      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Visible in directory</span>
                    </label>
                  </div>
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <button type="submit" data-testid={`member-save-${m.id}`} className="gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.2em] font-semibold px-6 py-2.5 hover:bg-gold-light transition-colors">Save Changes</button>
                  <button type="button" data-testid={`member-cancel-${m.id}`} onClick={() => setEditingId(null)} className="border border-gold/25 text-stone-400 font-condensed uppercase tracking-[0.2em] px-6 py-2.5 hover:text-gold hover:border-gold/50 transition-colors">Cancel</button>
                </div>
              </form>
            )}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-stone-500 text-sm px-5 py-8" data-testid="members-tab-empty">No members match.</p>}
      </div>
    </div>
  );
}

function FinancesTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/finances").then(({ data }) => setData(data)).catch(() => toast.error("Could not load finances"));
  useEffect(() => { load(); }, []);

  const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
  const kindStyle = { donation: "text-gold border-gold/40", store: "text-parchment border-stone-500/40", dues: "text-crimson border-crimson/40" };

  if (!data) return <div className="h-28 bg-card2/50 border border-gold/10 animate-pulse" data-testid="finances-loading" />;
  const { summary, donations, ledger } = data;

  return (
    <div data-testid="admin-finances-tab">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        {[
          { id: "grand", label: "Total Income", value: summary.grand_total, accent: true },
          { id: "donations", label: "Donations", value: summary.donations_total },
          { id: "store", label: "Armory Sales", value: summary.store_total },
          { id: "dues", label: "Member Dues", value: summary.dues_total },
          { id: "monthly", label: "Monthly Giving", value: summary.monthly_recurring },
        ].map((c) => (
          <div key={c.id} className={`border p-5 ${c.accent ? "border-gold/50 bg-gold/10" : "border-gold/20 bg-card2/40"}`} data-testid={`finance-card-${c.id}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{c.label}</p>
            <p className={`mt-2 font-display text-2xl ${c.accent ? "text-gold" : "text-parchment"}`}>{money(c.value)}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <h3 className="font-display text-lg uppercase tracking-wider text-parchment mb-4">Gifts Received</h3>
          <div className="border border-gold/15 divide-y divide-gold/10" data-testid="donations-list">
            {donations.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5 bg-card2/40" data-testid={`donation-row-${d.id}`}>
                <div className="min-w-[160px]">
                  <span className="font-condensed uppercase tracking-wider text-parchment">{d.donor_name || "Anonymous"}</span>
                  {d.donor_email && <div className="font-mono text-[10px] text-stone-500">{d.donor_email}</div>}
                </div>
                <div className="flex-1 font-mono text-[10px] uppercase tracking-widest text-stone-400">
                  {d.cause}{d.tier ? ` • ${d.tier}` : ""}
                  {d.recurring && <span className="ml-2 text-gold border border-gold/40 px-1.5 py-0.5">Monthly</span>}
                </div>
                <span className="font-display text-lg text-gold">{money(d.amount)}</span>
                <span className="font-mono text-[10px] text-stone-500 w-20 text-right">{fmtWhen(d.created_at)}</span>
              </div>
            ))}
            {donations.length === 0 && <p className="text-stone-500 text-sm px-5 py-8" data-testid="donations-empty">No gifts recorded yet.</p>}
          </div>
        </div>

        <div>
          <h3 className="font-display text-lg uppercase tracking-wider text-parchment mb-4">Income Ledger — All Sources</h3>
          <div className="border border-gold/15 divide-y divide-gold/10" data-testid="income-ledger">
            {ledger.map((l, i) => (
              <div key={i} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 bg-card2/40" data-testid={`ledger-row-${i}`}>
                <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${kindStyle[l.kind]}`}>{l.kind}</span>
                <div className="flex-1 min-w-[140px]">
                  <span className="text-sm text-stone-300">{l.label}</span>
                  <span className="font-mono text-[10px] text-stone-500 ml-2">{l.who}</span>
                </div>
                <span className="font-condensed text-parchment tracking-wider">{money(l.amount)}</span>
                <span className="font-mono text-[10px] text-stone-500 w-20 text-right">{fmtWhen(l.created_at)}</span>
              </div>
            ))}
            {ledger.length === 0 && <p className="text-stone-500 text-sm px-5 py-8" data-testid="ledger-empty">No income recorded yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) return <main className="pt-16 min-h-screen flex items-center justify-center"><p className="font-mono text-sm text-gold tracking-[0.3em] uppercase animate-pulse">Verifying credentials...</p></main>;
  if (!user || user.role !== "admin") return <Navigate to="/members" replace />;

  return (
    <main className="pt-16 min-h-screen" data-testid="admin-page">
      <section className="border-b border-crimson/25 noise-overlay crosshair-grid">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-crimson mb-3">Officers Only</p>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wide text-parchment">Command <span className="text-gold">Post</span></h1>
          <p className="mt-3 text-sm text-stone-400 max-w-lg">Manage the armory, the calendar, the gallery, the shipments — and those who would join the order.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <Tabs defaultValue="applications" data-testid="admin-tabs">
          <TabsList className="bg-transparent border border-gold/20 rounded-none h-auto p-1 flex flex-wrap">
            {[
              { v: "applications", label: "Applications", icon: FileText },
              { v: "members", label: "Members", icon: Users },
              { v: "events", label: "Events", icon: CalendarDays },
              { v: "products", label: "Armory", icon: Package },
              { v: "announcements", label: "Announcements", icon: Megaphone },
              { v: "gallery", label: "Gallery", icon: ImagePlus },
              { v: "devotions", label: "Devotions", icon: BookOpen },
              { v: "reports", label: "Reports", icon: BarChart3 },
              { v: "orders", label: "Orders", icon: Truck },
              { v: "finances", label: "Finances", icon: HandCoins },
            ].map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`admin-tab-${t.v}`} className="rounded-none font-condensed uppercase tracking-[0.15em] text-sm px-4 py-2.5 data-[state=active]:bg-gold data-[state=active]:text-obsidian data-[state=active]:font-semibold data-[state=active]:shadow-[0_0_15px_rgba(178,34,34,0.35)] text-stone-400">
                <t.icon className="w-4 h-4 mr-2 hidden sm:inline" />{t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="mt-10">
            <TabsContent value="applications"><Reveal><ApplicationsTab /></Reveal></TabsContent>
            <TabsContent value="members"><Reveal><MembersTab /></Reveal></TabsContent>
            <TabsContent value="events"><Reveal><EventsTab /></Reveal></TabsContent>
            <TabsContent value="products"><Reveal><ProductsTab /></Reveal></TabsContent>
            <TabsContent value="announcements"><Reveal><AnnouncementsTab /></Reveal></TabsContent>
            <TabsContent value="gallery"><Reveal><GalleryTab /></Reveal></TabsContent>
            <TabsContent value="devotions"><Reveal><DevotionsTab /></Reveal></TabsContent>
            <TabsContent value="reports"><Reveal><ReportsTab /></Reveal></TabsContent>
            <TabsContent value="orders"><Reveal><OrdersTab /></Reveal></TabsContent>
            <TabsContent value="finances"><Reveal><FinancesTab /></Reveal></TabsContent>
          </div>
        </Tabs>
      </section>
    </main>
  );
}
